/**
 * F13.8 — IMPORTACIONES MAESTRAS (una sola experiencia para unidades, familias, almacenes y artículos)
 *
 *   /inventario/importaciones            Importaciones (historial) · Sistemas de origen · Referencias
 *   /inventario/importaciones/nueva      1 maestro · 2 origen · 3 plantilla o fichero · 4 columnas → analizar
 *   /inventario/importaciones/:id        preview → corregir / decidir / excluir → aplicar → resultado
 *
 * Sustituye a «Importar catálogo ERP» (F13.1): el origen (NeatOS estándar, SAP, SAGE, otro) es sólo de dónde
 * vienen los códigos; el maestro es lo que se importa. No hay cuatro pantallas: el maestro es un parámetro
 * (`?tipo=ARTICULOS`), y Artículos / Almacenes / Configuración abren este mismo flujo con él preseleccionado.
 * El menú no cambia (F13.7: «El menú representa dominios funcionales, no funcionalidades individuales»).
 *
 * Nada se aplica sin preview; las filas con error, conflicto o coincidencia sin decidir NUNCA se saltan solas
 * (se corrige el fichero, se decide o se excluyen escribiendo cuántas). La API y la base son la barrera.
 */
import { useState, type FormEvent, type ReactNode } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import {
  api,
  FalloApi,
  ETIQUETA_DOMINIO,
  type AliasExterno,
  type ArticuloResumen,
  type ClasificacionImportacion,
  type DominioMaestro,
  type FamiliaArticulo,
  type FilaImportacion,
  type ImportacionMaestra,
  type MaestrosPorDominio,
  type ModoImportacion,
  type MuestraPreviewMaestro,
  type OpcionesInventario,
  type PreviewFicheroImportacion,
  type PreviewMaestro,
  type SistemaExterno,
  type TipoImportable,
  type TipoMaestro,
} from '../api.ts';
import { ConfirmDialog, Error as ErrorCaja, fechaHora, Modal, Panel, useDatos, Vacio } from '../componentes/base.tsx';
import { Shell } from '../componentes/shell.tsx';
import { useSesion } from '../contexto.tsx';
import { codigoVisible } from '../componentes/codigo-articulo.tsx';
import { guardarFichero } from './exportaciones.tsx';

const PERMISO = 'importaciones_maestras:gestionar';
const BASE = '/api/inventario/importaciones';
const MIGAS_BASE = [{ texto: 'Inventario' }, { texto: 'Artículos', a: '/inventario/articulos' }, { texto: 'Importaciones', a: '/inventario/importaciones' }];

export const CLASIFICACIONES: readonly (readonly [ClasificacionImportacion, string, string])[] = [
  ['CREAR', 'Alta', 'exito'],
  ['ACTUALIZAR', 'Actualiza', 'info'],
  ['REACTIVAR', 'Reactiva', 'info'],
  ['REASIGNAR', 'Reasigna', 'info'],
  ['SIN_CAMBIOS', 'Sin cambios', 'neutra'],
  ['DESACTIVAR', 'Baja', 'aviso'],
  ['CONFLICTO', 'Conflicto', 'error'],
  ['ERROR', 'Error', 'error'],
  ['POSIBLE_COINCIDENCIA', 'Posible coincidencia', 'aviso'],
  ['IGNORADA', 'Ignorada', 'neutra'],
];
const ETIQUETA = Object.fromEntries(CLASIFICACIONES.map(([k, t, c]) => [k, { t, c }])) as Record<ClasificacionImportacion, { t: string; c: string }>;

const MODOS: Readonly<Record<ModoImportacion, readonly [string, string]>> = {
  UPSERT: ['Altas y actualizaciones', 'Crea lo nuevo y actualiza lo que ya existe. No da de baja nada.'],
  SOLO_ALTAS: ['Sólo altas', 'Crea lo nuevo. A lo existente sólo le añade su código del origen (y sus códigos de barras).'],
  SINCRONIZACION_COMPLETA: ['Sincronización completa', 'Además, propone dar de baja (nunca borrar) los artículos de este origen que ya no vienen en el fichero.'],
};

export function Clasificacion({ c }: { c: ClasificacionImportacion }): ReactNode {
  return <span className={`etiqueta ${ETIQUETA[c].c}`}>{ETIQUETA[c].t}</span>;
}

export function EstadoImportacion({ i }: { i: Pick<ImportacionMaestra, 'estado' | 'caducada'> }): ReactNode {
  if (i.estado === 'PREVIEW' && i.caducada) return <span className="etiqueta aviso">Preview caducado</span>;
  const m: Record<ImportacionMaestra['estado'], readonly [string, string]> = { PREVIEW: ['Pendiente de aplicar', 'aviso'], APLICADA: ['Aplicada', 'exito'], CANCELADA: ['Cancelada', 'neutra'] } as const;
  return <span className={`etiqueta ${m[i.estado][1]}`}>{m[i.estado][0]}</span>;
}

const origenDe = (i: Pick<ImportacionMaestra, 'sistema'>): string => (i.sistema === null ? 'NeatOS estándar' : `${i.sistema.nombre} (${i.sistema.codigo})`);

function resumenTexto(i: ImportacionMaestra): string {
  const k = i.contadores;
  if (i.estado === 'APLICADA' && i.resultado !== null) {
    return `${String(i.resultado.APLICADA)} aplicadas · ${String(i.resultado.OMITIDA)} sin cambios · ${String(i.resultado.EXCLUIDA)} excluidas`;
  }
  return `${String(k.total)} filas · ${String(k.CREAR)} altas · ${String(k.ACTUALIZAR + k.REACTIVAR + k.REASIGNAR)} cambios · ${String(i.bloqueadas)} con problemas`;
}

function Pestanas({ opciones, actual, fijar }: { opciones: readonly (readonly [string, string])[]; actual: string; fijar: (k: string) => void }): ReactNode {
  return (
    <nav className="tabs" role="tablist" aria-label="Secciones de importaciones">
      {opciones.map(([k, t]) => (
        <a key={k} href={`?tab=${k}`} role="tab" aria-selected={actual === k} className={actual === k ? 'activo' : ''}
          onClick={(ev) => { ev.preventDefault(); fijar(k); }}>{t}</a>
      ))}
    </nav>
  );
}

/** Descarga un fichero de la API (plantilla, informe de errores) con su nombre. */
async function descargar(ruta: string): Promise<void> {
  const f = await api.fichero(ruta);
  guardarFichero(f.blob, f.nombre);
}

// ================================================================ página principal

export function PaginaImportacionesMaestras(): ReactNode {
  const { puede } = useSesion();
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') ?? 'importaciones';
  const sistemas = useDatos(() => api.get<{ filas: SistemaExterno[] }>('/api/inventario/sistemas-externos'), []);
  const pestanas: [string, string][] = [['importaciones', 'Importaciones'], ['sistemas', 'Sistemas de origen'], ['referencias', 'Referencias']];
  return (
    <Shell dominio="articulos" migas={[{ texto: 'Inventario' }, { texto: 'Artículos', a: '/inventario/articulos' }, { texto: 'Importaciones' }]} titulo="Importaciones"
      acciones={tab === 'importaciones' && puede(PERMISO) ? <Link className="boton principal" to="/inventario/importaciones/nueva">Nueva importación</Link> : undefined}
      tabs={<Pestanas opciones={pestanas} actual={tab} fijar={(k) => setParams({ tab: k })} />}>
      {tab === 'importaciones' && <Historial />}
      {tab === 'sistemas' && <SeccionSistemas alCambiar={sistemas.recargar} />}
      {tab === 'referencias' && <SeccionReferencias sistemas={sistemas.datos?.filas ?? []} />}
    </Shell>
  );
}

function Historial(): ReactNode {
  const [tipo, setTipo] = useState<TipoMaestro | ''>('');
  const lista = useDatos(() => api.get<{ filas: ImportacionMaestra[] }>(`${BASE}${tipo === '' ? '' : `?tipo=${tipo}`}`), [tipo]);
  return (
    <div className="panel">
      <div className="panel-cuerpo fila" style={{ flexWrap: 'wrap', gap: 8 }}>
        <label className="campo"><span>Maestro</span>
          <select value={tipo} onChange={(ev) => setTipo(ev.target.value as TipoMaestro | '')} aria-label="Filtrar por maestro">
            <option value="">Todos</option>
            {(['UNIDADES', 'FAMILIAS', 'ALMACENES', 'ARTICULOS'] as const).map((t) => <option key={t} value={t}>{ETIQUETA_DOMINIO[t]}</option>)}
          </select>
        </label>
      </div>
      <Panel estado={lista}>
        {(d) => (d.filas.length === 0 ? <Vacio titulo="Aún no hay importaciones" /> : (
          <div className="tabla-desliza">
            <table aria-label="Historial de importaciones" className="tabla-tarjetas">
              <thead><tr><th>Número</th><th>Maestro</th><th>Origen</th><th>Archivo</th><th>Fecha</th><th>Usuario</th><th>Resultado</th><th>Estado</th></tr></thead>
              <tbody>
                {d.filas.map((i) => (
                  <tr key={i.id}>
                    <td className="mono celda-titulo"><Link to={`/inventario/importaciones/${i.id}`}>{i.numero}</Link></td>
                    <td data-label="Maestro">{i.etiqueta}</td>
                    <td data-label="Origen">{origenDe(i)}</td>
                    <td data-label="Archivo" style={{ overflowWrap: 'anywhere' }}>{i.nombreArchivo}</td>
                    <td data-label="Fecha" className="pequena">{fechaHora(i.aplicadoEn ?? i.creadoEn)}</td>
                    <td data-label="Usuario">{i.aplicadoPor ?? i.creadoPor}</td>
                    <td data-label="Resultado" className="pequena">{resumenTexto(i)}</td>
                    <td data-label="Estado"><EstadoImportacion i={i} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      </Panel>
    </div>
  );
}

// ================================================================ nueva importación

const TIPOS: readonly TipoMaestro[] = ['UNIDADES', 'FAMILIAS', 'ALMACENES', 'ARTICULOS'];

export function PaginaNuevaImportacionMaestra(): ReactNode {
  const { puede } = useSesion();
  const navegar = useNavigate();
  const [params, setParams] = useSearchParams();
  const pedido = (params.get('tipo') ?? '').toUpperCase();
  const tipo: TipoMaestro | null = (TIPOS as readonly string[]).includes(pedido) ? (pedido as TipoMaestro) : null;
  const tipos = useDatos(() => api.get<{ tipos: TipoImportable[] }>(`${BASE}/tipos`), []);
  const sistemas = useDatos(() => api.get<{ filas: SistemaExterno[] }>('/api/inventario/sistemas-externos'), []);
  const def = tipos.datos?.tipos.find((t) => t.tipo === tipo) ?? null;
  const [idSistema, setIdSistema] = useState('');
  const [modo, setModo] = useState<ModoImportacion>('UPSERT');
  const [fichero, setFichero] = useState<File | null>(null);
  const [columnas, setColumnas] = useState<PreviewFicheroImportacion | null>(null);
  const [mapeo, setMapeo] = useState<Record<string, string>>({});
  const [error, setError] = useState<unknown>(null);
  const [ocupado, setOcupado] = useState(false);
  const activos = (sistemas.datos?.filas ?? []).filter((s) => s.activo);
  const sistema = activos.find((s) => s.id === idSistema) ?? null;

  function elegirTipo(t: TipoMaestro): void {
    setParams({ tipo: t }, { replace: true });
    setColumnas(null); setMapeo({}); setModo('UPSERT'); setError(null);
  }
  async function leer(ev?: FormEvent): Promise<void> {
    ev?.preventDefault();
    if (fichero === null || tipo === null || ocupado) return;
    setError(null); setOcupado(true);
    try {
      const fd = new FormData();
      fd.append('tipo', tipo);
      if (idSistema !== '') fd.append('idSistema', idSistema);
      fd.append('fichero', fichero);
      const c = await api.subir<PreviewFicheroImportacion>(`${BASE}/preview`, fd);
      setColumnas(c); setMapeo(c.mapeoSugerido);
    } catch (e) {
      setError(e);
    } finally {
      setOcupado(false);
    }
  }
  async function analizar(): Promise<void> {
    if (fichero === null || tipo === null) return;
    setError(null); setOcupado(true);
    try {
      const fd = new FormData();
      fd.append('tipo', tipo);
      if (idSistema !== '') fd.append('idSistema', idSistema);
      fd.append('modo', modo);
      fd.append('mapeo', JSON.stringify(mapeo));
      fd.append('fichero', fichero);
      const r = await api.subir<ImportacionMaestra>(BASE, fd);
      navegar(`/inventario/importaciones/${r.id}`);
    } catch (e) {
      setError(e);
      setOcupado(false);
    }
  }
  const tieneCodigo = Object.values(mapeo).filter((v) => v === 'codigo').length === 1;
  const faltan = def === null ? [] : def.campos.filter((c) => c.obligatorio && !Object.values(mapeo).includes(c.campo)).map((c) => c.etiqueta);

  if (!puede(PERMISO)) {
    return <Shell dominio="articulos" migas={[...MIGAS_BASE, { texto: 'Nueva' }]} titulo="Nueva importación"><div className="panel"><Vacio titulo="No tienes permiso para importar maestros" /></div></Shell>;
  }
  return (
    <Shell dominio="articulos" migas={[...MIGAS_BASE, { texto: 'Nueva' }]} titulo={def === null ? 'Nueva importación' : `Nueva importación · ${def.etiqueta}`}>
      {error !== null && <ErrorCaja error={error} />}
      <section className="panel" aria-labelledby="paso-maestro">
        <div className="panel-cabecera"><h2 id="paso-maestro">1 · Qué maestro importas</h2></div>
        <div className="panel-cuerpo">
          <div className="fila opciones-maestro" role="radiogroup" aria-label="Maestro">
            {TIPOS.map((t) => (
              <label key={t} className="fila opcion-grande">
                <input type="radio" name="maestro" checked={tipo === t} onChange={() => elegirTipo(t)} /> {ETIQUETA_DOMINIO[t]}
              </label>
            ))}
          </div>
          {def !== null && def.dependencias.length > 0 && (
            <p className="suave pequena">Deben existir antes: {def.dependencias.join(', ')}. Nunca se crean automáticamente desde aquí.</p>
          )}
          <p className="suave pequena">Orden recomendado: Unidades → Familias → Almacenes → Artículos.</p>
        </div>
      </section>

      {def !== null && (
        <section className="panel" aria-labelledby="paso-origen">
          <div className="panel-cabecera"><h2 id="paso-origen">2 · De dónde vienen los códigos</h2></div>
          <div className="panel-cuerpo">
            <label className="campo"><span>Origen</span>
              <select value={idSistema} onChange={(ev) => { setIdSistema(ev.target.value); setColumnas(null); if (ev.target.value === '' && modo === 'SINCRONIZACION_COMPLETA') setModo('UPSERT'); }}>
                <option value="">NeatOS estándar (los códigos son los de NeatOS)</option>
                {activos.map((s) => {
                  const maestroAqui = (s.dominiosMaestro ?? []).includes(def.tipo);
                  return <option key={s.id} value={s.id}>{s.nombre} ({s.codigo}){maestroAqui ? ` — maestro de ${ETIQUETA_DOMINIO[def.tipo].toLowerCase()}` : ''}</option>;
                })}
              </select>
            </label>
            <p className="suave pequena">
              {sistema === null ? 'El código del fichero es el de NeatOS.'
                : (sistema.dominiosMaestro ?? []).includes(def.tipo) ? `${sistema.codigo} es el maestro: sus códigos pasan a ser los de NeatOS (auditado).`
                  : `${sistema.codigo} no es el maestro: se crean o mantienen sus referencias externas; el código de NeatOS no cambia.`}
              {' '}No es un conector: sólo se lee el fichero que subes.
            </p>
            {def.modos.length > 1 && (
              <fieldset className="campo">
                <legend>Modo</legend>
                {def.modos.filter((m) => m !== 'SINCRONIZACION_COMPLETA' || idSistema !== '').map((m) => (
                  <label key={m} className="fila opcion-grande" style={{ alignItems: 'flex-start' }}>
                    <input type="radio" name="modo" value={m} checked={modo === m} onChange={() => setModo(m)} />
                    <span><strong>{MODOS[m][0]}</strong><br /><span className="suave pequena">{MODOS[m][1]}</span></span>
                  </label>
                ))}
              </fieldset>
            )}
          </div>
        </section>
      )}

      {def !== null && (
        <section className="panel" aria-labelledby="paso-fichero">
          <div className="panel-cabecera"><h2 id="paso-fichero">3 · Plantilla o fichero</h2></div>
          <form className="panel-cuerpo" onSubmit={(ev) => void leer(ev)}>
            <p className="pequena">
              <button type="button" onClick={() => void descargar(`${BASE}/plantillas/${def.tipo}`).catch(setError)}>Descargar {def.plantilla.nombre}</button>
              {' '}<span className="suave">Columnas de código en formato Texto (los ceros se conservan), instrucciones y valores permitidos. Máximo {def.maxFilas} filas.</span>
            </p>
            <label className="campo"><span>Fichero (.csv o .xlsx)</span>
              <input type="file" accept=".csv,.txt,.xlsx" onChange={(ev) => { setFichero(ev.target.files?.[0] ?? null); setColumnas(null); }} />
            </label>
            <button type="submit" disabled={ocupado || fichero === null}>{ocupado && columnas === null ? 'Leyendo…' : 'Leer el fichero'}</button>
          </form>
        </section>
      )}

      {def !== null && columnas !== null && (
        <section className="panel" aria-labelledby="paso-columnas">
          <div className="panel-cabecera"><h2 id="paso-columnas">4 · Columnas</h2></div>
          <div className="panel-cuerpo">
            <p className="suave pequena">{columnas.formato} · {columnas.totalFilas} filas.
              {columnas.plantilla !== null ? <> Plantilla oficial <span className="mono">{columnas.plantilla}</span>: las columnas ya están asignadas.</> : ' Indica qué es cada columna; las que no asignes se ignoran.'}</p>
            {columnas.avisos.map((a) => <p key={a} className="nota aviso pequena">{a}</p>)}
            {columnas.mismoFicheroAplicado !== null && <p className="nota aviso pequena">Este mismo fichero ya se aplicó en {columnas.mismoFicheroAplicado}: si nada ha cambiado, todo saldrá sin cambios.</p>}
            <div className="tabla-desliza">
              <table aria-label="Columnas del fichero" className="tabla-tarjetas">
                <thead><tr><th>Columna del fichero</th><th>Ejemplo</th><th>Campo de NeatOS</th></tr></thead>
                <tbody>
                  {columnas.cabeceras.map((cab, i) => (
                    <tr key={cab}>
                      <td className="celda-titulo">{cab}</td>
                      <td data-label="Ejemplo" className="mono pequena" style={{ overflowWrap: 'anywhere' }}>{columnas.muestra[0]?.[i] ?? ''}</td>
                      <td data-label="Campo">
                        <select aria-label={`Campo para la columna ${cab}`} value={mapeo[cab] ?? ''} onChange={(ev) => setMapeo({ ...mapeo, [cab]: ev.target.value })}>
                          <option value="">Ignorar</option>
                          {columnas.campos.map((c) => <option key={c.campo} value={c.campo}>{c.etiqueta}</option>)}
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {!tieneCodigo && <p className="error-campo">Asigna exactamente una columna al código.</p>}
            {tieneCodigo && faltan.length > 0 && <p className="nota aviso pequena">Sin {faltan.join(', ')} sólo se podrán actualizar los existentes: para dar de alta hacen falta.</p>}
            <div className="fila" style={{ marginTop: 12, flexWrap: 'wrap' }}>
              <button className="principal" onClick={() => void analizar()} disabled={ocupado || !tieneCodigo}>{ocupado ? 'Analizando…' : 'Ver la vista previa (no cambia nada)'}</button>
            </div>
          </div>
        </section>
      )}
    </Shell>
  );
}

// ================================================================ preview / resultado

function textoAccion(f: FilaImportacion): string {
  const c = f.accion;
  if (c === null) return '';
  const partes: string[] = [];
  if (c.crear) {
    const d = [c.crear.codigo, c.crear.descripcion, c.crear.unidad, c.crear.familia, c.crear.tipo, c.crear.centro].filter((x) => x !== undefined && x !== null && x !== '');
    partes.push(`Alta: ${d.join(' · ')}`);
  }
  if (c.codigo) partes.push(`Código: ${c.codigo.antes} → ${c.codigo.despues} (auditado)`);
  if (c.descripcion) partes.push(`Descripción: «${c.descripcion.antes}» → «${c.descripcion.despues}»`);
  if (c.unidad) partes.push(`Unidad: ${c.unidad.antes.codigo} → ${c.unidad.despues.codigo}`);
  if (c.familia) partes.push(`Familia: ${c.familia.antes.codigo} → ${c.familia.despues.codigo}`);
  for (const [k, v] of Object.entries(c.extra ?? {})) partes.push(`${k}: ${String(v.antes)} → ${String(v.despues)}`);
  if (c.activo) partes.push(c.activo.despues ? 'Se reactiva' : 'Se da de baja (no se borra)');
  if (c.referencia) partes.push(`Referencia del origen: ${c.referencia}`);
  if (c.codigoNegocio) partes.push(c.codigoNegocio.antes === null ? `Código de negocio: ${c.codigoNegocio.despues}` : `Código de negocio: ${c.codigoNegocio.antes} → ${c.codigoNegocio.despues}`);
  if (c.codigos && c.codigos.length > 0) partes.push(`Añade ${c.codigos.map((x) => x.valor).join(', ')}`);
  if (c.reasignar) partes.push(`Reasigna el código: la referencia de ${c.reasignar.codigoArticulo} queda en su histórico`);
  return partes.join(' · ');
}

export function PaginaImportacionMaestra(): ReactNode {
  const { id = '' } = useParams();
  const { puede } = useSesion();
  const [filtro, setFiltro] = useState<ClasificacionImportacion | 'PROBLEMAS' | ''>('');
  const [texto, setTexto] = useState('');
  const [ronda, setRonda] = useState(0);
  const ruta = `${BASE}/${encodeURIComponent(id)}`;
  const imp = useDatos(() => api.get<ImportacionMaestra>(ruta), [ruta, ronda]);
  const filas = useDatos(() => {
    const p = new URLSearchParams({ porPagina: '200' });
    if (filtro === 'PROBLEMAS') p.set('problemas', 'true');
    else if (filtro !== '') p.set('clasificacion', filtro);
    if (texto.trim() !== '') p.set('texto', texto.trim());
    return api.get<{ filas: FilaImportacion[]; total: number }>(`${ruta}/filas?${p.toString()}`);
  }, [ruta, filtro, texto, ronda]);
  const [error, setError] = useState<unknown>(null);
  const [ocupado, setOcupado] = useState(false);
  const [confirmar, setConfirmar] = useState<'aplicar' | 'cancelar' | null>(null);
  const [excluir, setExcluir] = useState(false);
  const [cuantas, setCuantas] = useState('');
  const [numeroBajas, setNumeroBajas] = useState('');
  const i = imp.datos;
  const abierta = i?.estado === 'PREVIEW' && puede(PERMISO);

  async function accion(f: () => Promise<unknown>): Promise<boolean> {
    setError(null); setOcupado(true);
    try {
      await f();
      setRonda((r) => r + 1);
      return true;
    } catch (e) {
      setError(e);
      if (e instanceof FalloApi && e.codigo === 'PREVIEW_CADUCADA') setRonda((r) => r + 1);
      return false;
    } finally {
      setOcupado(false);
    }
  }
  const decidir = (cuerpo: Record<string, unknown>) => void accion(() => api.post(`${ruta}/decisiones`, cuerpo));
  const entidadUrl = (e: { id: string }): string | null => (i?.tipo === 'ARTICULOS' ? `/inventario/articulos/${e.id}` : i?.tipo === 'ALMACENES' ? `/inventario/almacenes/${e.id}` : null);

  return (
    <Shell dominio="articulos" migas={[...MIGAS_BASE, { texto: i?.numero ?? '…' }]}
      titulo={i === null ? 'Importación' : <><span className="mono">{i.numero}</span> · {i.etiqueta}</>}
      estado={i === null ? undefined : <EstadoImportacion i={i} />}
      acciones={abierta ? (
        <div className="fila" style={{ flexWrap: 'wrap' }}>
          <button onClick={() => void accion(() => api.post(`${ruta}/reanalizar`))} disabled={ocupado}>Volver a analizar</button>
          <button onClick={() => setConfirmar('cancelar')} disabled={ocupado}>Cancelar</button>
          <button className="principal" onClick={() => { setExcluir(false); setCuantas(''); setNumeroBajas(''); setConfirmar('aplicar'); }}
            disabled={ocupado || (i?.caducada ?? false)}>Aplicar</button>
        </div>
      ) : undefined}>
      {error !== null && <ErrorCaja error={error} />}
      <Panel estado={imp}>
        {(d) => (
          <div className="panel">
            <div className="panel-cuerpo">
              <p className="pequena">{d.etiqueta} · origen {origenDe(d)} · {MODOS[d.modo][0]} · {d.formato}{d.versionPlantilla !== null && <> · plantilla <span className="mono">{d.versionPlantilla}</span></>}
                {' '}· {d.contadores.total} filas · analizada por {d.creadoPor} el {fechaHora(d.creadoEn)}
                {d.aplicadoEn !== null && <> · <strong>aplicada por {d.aplicadoPor} el {fechaHora(d.aplicadoEn)}</strong></>}
                {d.canceladoEn !== null && <> · cancelada por {d.canceladoPor} el {fechaHora(d.canceladoEn)}</>}</p>
              <p className="suave pequena mono" title="SHA-256 del fichero" style={{ overflowWrap: 'anywhere' }}>{d.nombreArchivo} · SHA-256 {d.hashSha256}</p>
              {d.estado === 'PREVIEW' && !d.caducada && <p className="nota info pequena">Vista previa: todavía no se ha cambiado nada. Caduca el {fechaHora(d.caducaEn)}.</p>}
              {d.estado === 'PREVIEW' && d.caducada && <p className="nota aviso pequena">El preview ha caducado (más de 24 horas): vuelve a analizar para ver el efecto con los datos de ahora.</p>}
              {d.mismoFicheroAplicado !== null && d.estado === 'PREVIEW' && <p className="nota aviso pequena">Este mismo fichero ya se aplicó en {d.mismoFicheroAplicado}.</p>}
              {d.avisos.map((a) => <p key={a.codigo} className="nota aviso pequena">{a.mensaje}</p>)}
              {d.estado === 'PREVIEW' && d.bloqueadas > 0 && (
                <p className="nota aviso pequena">Hay {d.bloqueadas} filas con errores, conflictos o coincidencias sin decidir. No se saltan solas: corrige el fichero, decide en cada una o exclúyelas al aplicar.
                  {' '}<button onClick={() => void descargar(`${ruta}/errores?formato=csv`).catch(setError)}>Informe de errores (CSV)</button>
                  {' '}<button onClick={() => void descargar(`${ruta}/errores?formato=xlsx`).catch(setError)}>(XLSX)</button></p>
              )}
              {d.estado === 'APLICADA' && d.resultado !== null && (
                <p className="nota exito pequena" role="status">Aplicada: {d.resultado.APLICADA} filas aplicadas, {d.resultado.OMITIDA} sin cambios, {d.resultado.EXCLUIDA} excluidas explícitamente.</p>
              )}
              <div className="fila" role="group" aria-label="Filtrar por clasificación" style={{ flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
                <button className={filtro === '' ? 'principal' : ''} aria-pressed={filtro === ''} onClick={() => setFiltro('')}>Todas ({d.contadores.total + d.contadores.DESACTIVAR})</button>
                {d.bloqueadas > 0 && <button className={filtro === 'PROBLEMAS' ? 'principal' : ''} aria-pressed={filtro === 'PROBLEMAS'} onClick={() => setFiltro('PROBLEMAS')}>Con problemas ({d.bloqueadas})</button>}
                {CLASIFICACIONES.filter(([k]) => d.contadores[k] > 0).map(([k, t]) => (
                  <button key={k} className={filtro === k ? 'principal' : ''} aria-pressed={filtro === k} onClick={() => setFiltro(k)}>{t} ({d.contadores[k]})</button>
                ))}
              </div>
              {abierta && d.contadores.POSIBLE_COINCIDENCIA > 0 && (
                <div className="fila" style={{ flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
                  <span className="pequena">Posibles coincidencias en bloque:</span>
                  <button disabled={ocupado} onClick={() => decidir({ masiva: 'ACEPTAR_UNICAS' })}>Aceptar las de un único candidato</button>
                  <button disabled={ocupado} onClick={() => decidir({ masiva: 'CREAR' })}>Dar de alta todas</button>
                  <button disabled={ocupado} onClick={() => decidir({ masiva: 'IGNORAR' })}>Ignorar todas</button>
                </div>
              )}
            </div>
          </div>
        )}
      </Panel>
      <div className="panel">
        <div className="panel-cuerpo">
          <input type="search" aria-label="Buscar en la importación" placeholder="Código, descripción o código de NeatOS…" value={texto} onChange={(ev) => setTexto(ev.target.value)} />
        </div>
        <Panel estado={filas}>
          {(d) => (d.filas.length === 0 ? <Vacio titulo="Ninguna fila con este filtro" /> : (
            <div className="tabla-desliza">
              <table aria-label="Filas de la importación" className="tabla-tarjetas">
                <thead><tr><th>Fila</th><th>Código</th><th>Resultado</th><th>En NeatOS</th><th>Qué pasará</th>{i?.estado === 'APLICADA' && <th>Aplicada</th>}</tr></thead>
                <tbody>
                  {d.filas.map((f) => (
                    <tr key={f.id}>
                      <td className="celda-titulo mono celda-codigo">{f.numero === null ? 'Ausente del fichero' : `Fila ${String(f.numero)}`}</td>
                      <td data-label="Código" className="mono celda-codigo">{f.codigo ?? '—'}</td>
                      <td data-label="Resultado"><Clasificacion c={f.clasificacion} /></td>
                      <td data-label="En NeatOS">{f.entidad === null ? String(f.datos?.['descripcion'] ?? '—') : (
                        entidadUrl(f.entidad) === null ? <><span className="mono">{f.entidad.codigoNegocio}</span> · {f.entidad.descripcion}</>
                          : <Link to={entidadUrl(f.entidad)!}><span className="mono">{codigoVisible(f.entidad)}</span> · {f.entidad.descripcion}</Link>)}</td>
                      <td data-label="Qué pasará" style={{ textAlign: 'left' }}>
                        <div>
                          {textoAccion(f) !== '' && <div className="pequena">{textoAccion(f)}</div>}
                          {f.errores.map((e) => <div key={e.codigo + e.mensaje} className="error-campo"><span className="mono pequena">{e.codigo}</span> {e.mensaje}</div>)}
                          {f.avisos.map((a) => <div key={a.codigo + a.mensaje} className="suave pequena">{a.mensaje}</div>)}
                          {abierta && f.decisiones.includes('ACEPTAR') && f.clasificacion === 'POSIBLE_COINCIDENCIA' && (
                            <DecisionCoincidencia fila={f} habilitada={!ocupado} decidir={(c) => decidir({ idFila: f.id, ...c })} />
                          )}
                          {abierta && f.decisiones.includes('ACEPTAR') && f.decision !== null && f.clasificacion !== 'POSIBLE_COINCIDENCIA' && (
                            <p className="pequena">Decidido: {f.decision.decision === 'ACEPTAR' ? 'es uno existente' : f.decision.decision === 'CREAR' ? 'dar de alta' : 'ignorar'}.</p>
                          )}
                          {abierta && f.decisiones.includes('REACTIVAR') && (
                            <DecisionReferenciaInactiva fila={f} habilitada={!ocupado} decidir={(c) => decidir({ idFila: f.id, ...c })} />
                          )}
                        </div>
                      </td>
                      {i?.estado === 'APLICADA' && <td data-label="Aplicada">{f.resultado === 'APLICADA' ? <span className="etiqueta exito">Sí</span>
                        : f.resultado === 'EXCLUIDA' ? <span className="etiqueta aviso">Excluida</span> : <span className="etiqueta neutra">No</span>}</td>}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </Panel>
      </div>
      {confirmar === 'cancelar' && (
        <ConfirmDialog titulo="Cancelar esta importación" descripcion="No se aplicará nada. Queda en el historial como cancelada." confirmar="Cancelar la importación"
          ocupado={ocupado} cerrar={() => setConfirmar(null)}
          alConfirmar={async () => { if (await accion(() => api.post(`${ruta}/cancelar`))) setConfirmar(null); }} />
      )}
      {confirmar === 'aplicar' && i !== null && (
        <ConfirmDialog titulo="Aplicar la importación" variante="principal" confirmar="Aplicar" ocupado={ocupado} cerrar={() => setConfirmar(null)}
          descripcion={<>
            <p>Se aplicarán {i.aplicables} filas ({i.contadores.CREAR} altas, {i.contadores.ACTUALIZAR + i.contadores.REACTIVAR + i.contadores.REASIGNAR} cambios, {i.contadores.DESACTIVAR} bajas) en una sola operación: o todo, o nada. Nunca se borra nada.</p>
            {i.bloqueadas > 0 && (
              <>
                <p className="nota aviso">{i.bloqueadas} filas tienen errores, conflictos o coincidencias sin decidir. Para aplicar el resto hay que excluirlas explícitamente (quedan registradas).</p>
                <label className="fila"><input type="checkbox" checked={excluir} onChange={(ev) => setExcluir(ev.target.checked)} /> Excluir las filas con problemas</label>
                {excluir && (
                  <label className="campo"><span>Escribe cuántas filas se excluyen ({i.bloqueadas})</span>
                    <input inputMode="numeric" value={cuantas} onChange={(ev) => setCuantas(ev.target.value)} aria-label="Número de filas excluidas" />
                  </label>
                )}
              </>
            )}
            {i.requiereConfirmacionReforzada && (
              <label className="campo" style={{ marginTop: 12 }}><span>Esta importación da de baja muchos registros. Escribe el número de bajas para confirmar</span>
                <input inputMode="numeric" value={numeroBajas} onChange={(ev) => setNumeroBajas(ev.target.value)} aria-label="Número de bajas" />
              </label>
            )}
          </>}
          alConfirmar={async () => {
            const cuerpo: Record<string, unknown> = {};
            if (i.bloqueadas > 0) { cuerpo['excluirBloqueadas'] = excluir; cuerpo['filasExcluidas'] = Number(cuantas); }
            if (i.requiereConfirmacionReforzada) { cuerpo['confirmacionReforzada'] = true; cuerpo['desactivacionesConfirmadas'] = Number(numeroBajas); }
            if (await accion(() => api.post(`${ruta}/aplicar`, cuerpo))) setConfirmar(null);
          }} />
      )}
    </Shell>
  );
}

function DecisionCoincidencia({ fila: f, habilitada, decidir }: {
  fila: FilaImportacion; habilitada: boolean; decidir: (c: { decision: 'ACEPTAR' | 'CREAR' | 'IGNORAR'; idEntidad?: string }) => void;
}): ReactNode {
  const [elegido, setElegido] = useState(f.candidatos?.[0]?.id ?? '');
  return (
    <div className="fila" role="group" aria-label={`Decisión sobre ${f.codigo ?? ''}`} style={{ flexWrap: 'wrap', gap: 8, marginTop: 4 }}>
      <select aria-label={`Existente para ${f.codigo ?? ''}`} value={elegido} onChange={(ev) => setElegido(ev.target.value)} disabled={!habilitada}>
        {(f.candidatos ?? []).map((c) => <option key={c.id} value={c.id}>{codigoVisible(c)} · {c.descripcion}</option>)}
      </select>
      <button disabled={!habilitada || elegido === ''} onClick={() => decidir({ decision: 'ACEPTAR', idEntidad: elegido })}>Es este</button>
      <button disabled={!habilitada} onClick={() => decidir({ decision: 'CREAR' })}>Dar de alta</button>
      <button disabled={!habilitada} onClick={() => decidir({ decision: 'IGNORAR' })}>Ignorar</button>
    </div>
  );
}

/**
 * Código del origen cuya referencia es de un artículo DE BAJA. Tres salidas, todas explícitas: reactivar ese
 * artículo, reasignar el código (a uno nuevo con los datos de la fila o a otro existente activo) o dejarlo sin
 * resolver (y excluir la fila al aplicar).
 */
function DecisionReferenciaInactiva({ fila: f, habilitada, decidir }: {
  fila: FilaImportacion; habilitada: boolean; decidir: (c: { decision: 'REACTIVAR' | 'REASIGNAR' | 'SIN_RESOLVER'; idEntidad?: string }) => void;
}): ReactNode {
  const [buscando, setBuscando] = useState(false);
  const [texto, setTexto] = useState('');
  const [resultados, setResultados] = useState<ArticuloResumen[] | null>(null);
  const [elegido, setElegido] = useState('');
  const deBaja = f.accion?.reasignar?.codigoArticulo ?? (f.entidad === null ? '' : codigoVisible(f.entidad));
  async function buscar(): Promise<void> {
    const p = new URLSearchParams({ texto: texto.trim(), activo: 'true', porPagina: '10' });
    const r = await api.get<{ filas: ArticuloResumen[] }>(`/api/inventario/articulos?${p.toString()}`);
    const validos = r.filas.filter((a) => a.id !== f.accion?.reasignar?.idArticulo && a.id !== f.entidad?.id);
    setResultados(validos); setElegido(validos[0]?.id ?? '');
  }
  if (f.decision !== null) {
    const t = f.decision.decision === 'REACTIVAR' ? `Decidido: reactivar ${deBaja}.`
      : f.decision.idEntidad === null ? 'Decidido: reasignar el código a un artículo nuevo.' : 'Decidido: reasignar el código a otro artículo.';
    return (
      <div className="fila" role="group" aria-label={`Decisión sobre ${f.codigo ?? ''}`} style={{ flexWrap: 'wrap', gap: 8, marginTop: 4 }}>
        <span className="pequena">{t}</span>
        <button disabled={!habilitada} onClick={() => decidir({ decision: 'SIN_RESOLVER' })}>Deshacer decisión</button>
      </div>
    );
  }
  return (
    <div role="group" aria-label={`Decisión sobre ${f.codigo ?? ''}`} style={{ marginTop: 4 }}>
      <div className="fila" style={{ flexWrap: 'wrap', gap: 8 }}>
        <button disabled={!habilitada} onClick={() => decidir({ decision: 'REACTIVAR' })}>Es el mismo: reactivar {deBaja}</button>
        <button disabled={!habilitada} onClick={() => decidir({ decision: 'REASIGNAR' })}>Código reutilizado: artículo nuevo</button>
        <button disabled={!habilitada} aria-expanded={buscando} onClick={() => setBuscando(!buscando)}>Código reutilizado: otro artículo…</button>
      </div>
      {buscando && (
        <div className="fila" style={{ flexWrap: 'wrap', gap: 8, marginTop: 4 }}>
          <input type="search" aria-label="Buscar artículo de destino" placeholder="ART-…, código o descripción" value={texto} onChange={(ev) => setTexto(ev.target.value)} />
          <button disabled={!habilitada || texto.trim() === ''} onClick={() => void buscar()}>Buscar</button>
          {resultados !== null && (resultados.length === 0 ? <span className="suave pequena">Ningún artículo activo encontrado.</span> : (
            <>
              <select aria-label="Artículo de destino" value={elegido} onChange={(ev) => setElegido(ev.target.value)}>
                {resultados.map((a) => <option key={a.id} value={a.id}>{codigoVisible(a)} · {a.descripcion}</option>)}
              </select>
              <button disabled={!habilitada || elegido === ''} onClick={() => decidir({ decision: 'REASIGNAR', idEntidad: elegido })}>Reasignar a este</button>
            </>
          ))}
        </div>
      )}
    </div>
  );
}

// ================================================================ sistemas de origen

function SeccionSistemas({ alCambiar }: { alCambiar: () => void }): ReactNode {
  const { puede } = useSesion();
  const gestiona = puede('sistemas_externos:gestionar');
  const lista = useDatos(() => api.get<{ filas: SistemaExterno[] }>('/api/inventario/sistemas-externos'), []);
  const [alta, setAlta] = useState({ codigo: '', nombre: '', tipo: '' });
  const [error, setError] = useState<unknown>(null);
  const [ocupado, setOcupado] = useState(false);
  const maestros = useDatos(() => api.get<MaestrosPorDominio>('/api/inventario/maestros-dominio'), []);

  async function ejecutar(f: () => Promise<unknown>): Promise<boolean> {
    setError(null); setOcupado(true);
    try { await f(); lista.recargar(); maestros.recargar(); alCambiar(); return true; } catch (e) { setError(e); return false; } finally { setOcupado(false); }
  }
  async function crear(ev: FormEvent): Promise<void> {
    ev.preventDefault();
    if (await ejecutar(() => api.post('/api/inventario/sistemas-externos', { ...alta, tipo: alta.tipo === '' ? null : alta.tipo }))) setAlta({ codigo: '', nombre: '', tipo: '' });
  }

  return (
    <>
      {error !== null && <ErrorCaja error={error} />}
      <SeccionMaestros estado={maestros} sistemas={lista.datos?.filas ?? []} gestiona={gestiona} alCambiar={() => { maestros.recargar(); lista.recargar(); alCambiar(); }} />
      <div className="panel">
        <div className="panel-cabecera"><h2>Sistemas de origen</h2></div>
        <Panel estado={lista}>
          {(d) => (d.filas.length === 0 ? <Vacio titulo="No hay sistemas de origen" /> : (
            <div className="tabla-desliza">
              <table className="tabla-tarjetas" aria-label="Sistemas de origen">
                <thead><tr><th>Código</th><th>Nombre</th><th>Tipo</th><th>Referencias</th><th>Estado</th>{gestiona && <th aria-label="Acciones" />}</tr></thead>
                <tbody>
                  {d.filas.map((s) => (
                    <tr key={s.id}>
                      <td className="mono celda-titulo">{s.codigo}{(s.dominiosMaestro ?? []).map((x) => <span key={x}> <span className="etiqueta info">Maestro de {ETIQUETA_DOMINIO[x].toLowerCase()}</span></span>)}</td>
                      <td data-label="Nombre">{s.nombre}</td><td data-label="Tipo">{s.tipo ?? '—'}</td><td data-label="Referencias">{s.nReferencias}</td>
                      <td data-label="Estado">{s.activo ? <span className="etiqueta exito">Activo</span> : <span className="etiqueta neutra">Inactivo</span>}</td>
                      {gestiona && (
                        <td className="celda-accion">
                          {(s.dominiosMaestro ?? []).length === 0 && <button disabled={ocupado} onClick={() => void ejecutar(() => api.patch(`/api/inventario/sistemas-externos/${s.id}`, { activo: !s.activo }))}>{s.activo ? 'Desactivar' : 'Reactivar'}</button>}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </Panel>
      </div>
      {gestiona && (
        <div className="panel">
          <div className="panel-cabecera"><h2>Nuevo sistema de origen</h2></div>
          <form className="panel-cuerpo fila" style={{ flexWrap: 'wrap', alignItems: 'flex-end' }} onSubmit={(ev) => void crear(ev)}>
            <label className="campo"><span>Código</span><input value={alta.codigo} required placeholder="SAP_ES01" onChange={(ev) => setAlta({ ...alta, codigo: ev.target.value.toUpperCase() })} /></label>
            <label className="campo"><span>Nombre</span><input value={alta.nombre} required onChange={(ev) => setAlta({ ...alta, nombre: ev.target.value })} /></label>
            <label className="campo"><span>Tipo (opcional)</span>
              <input value={alta.tipo} list="tipos-erp" onChange={(ev) => setAlta({ ...alta, tipo: ev.target.value })} />
              <datalist id="tipos-erp"><option value="SAP" /><option value="SAGE200" /><option value="BUSINESS_CENTRAL" /><option value="OTRO" /></datalist>
            </label>
            <button type="submit" className="principal" disabled={ocupado}>Dar de alta</button>
          </form>
        </div>
      )}
    </>
  );
}

// ================================================================ sistema maestro por dominio (F13.5-A, real en los cuatro desde F13.8)

const TEXTO_BLOQUEO: Readonly<Record<string, string>> = {
  COLISIONES: 'Hay códigos que chocan con otros (repetidos, intercambiados o con la forma de un código NeatOS): corrígelos en el sistema antes de cambiar.',
  SISTEMA_INACTIVO: 'El sistema está desactivado.',
  SIN_CAMBIO: 'Ese ya es el sistema maestro.',
};
const TEXTO_EXCEPCION: Readonly<Record<string, string>> = {
  COBERTURA_INCOMPLETA: 'Hay registros sin código en el sistema nuevo: volverán a su código de NeatOS (ART-… / ALM-…).',
  REFERENCIAS_INACTIVAS: 'Algunos registros sólo tienen una referencia desactivada en el sistema nuevo.',
  REUTILIZACIONES: 'Algunos códigos fueron antes el código de otro registro.',
};

function SeccionMaestros({ estado, sistemas, gestiona, alCambiar }: {
  estado: { datos: MaestrosPorDominio | null; cargando: boolean; error: unknown; recargar: () => void }; sistemas: readonly SistemaExterno[]; gestiona: boolean; alCambiar: () => void;
}): ReactNode {
  const [dominio, setDominio] = useState<DominioMaestro | null>(null);
  return (
    <div className="panel">
      <div className="panel-cabecera"><h2>Sistema maestro por dominio</h2></div>
      <div className="panel-cuerpo">
        <p className="suave pequena">El código que se ve de un artículo, almacén, familia o unidad es el de su sistema maestro (o, sin él, el de NeatOS). Cambiar de maestro
          recodifica en una sola operación, con preview antes y auditado, y nunca cambia su identidad, su stock, su historial ni sus etiquetas impresas.</p>
      </div>
      <Panel estado={estado}>
        {(d) => (
          <div className="tabla-desliza">
            <table className="tabla-tarjetas" aria-label="Sistema maestro por dominio">
              <thead><tr><th>Dominio</th><th>Maestro vigente</th><th>Desde</th>{gestiona && <th aria-label="Acciones" />}</tr></thead>
              <tbody>
                {d.dominios.map((x) => (
                  <tr key={x.dominio}>
                    <td className="celda-titulo">{ETIQUETA_DOMINIO[x.dominio]}</td>
                    <td data-label="Maestro">{x.vigente === null ? <span className="suave">Sin maestro</span> : <>{x.vigente.sistema.nombre} <span className="mono suave pequena">{x.vigente.sistema.codigo}</span>
                      {x.vigente.excepcion && <> <span className="etiqueta aviso" title={x.vigente.motivoExcepcion ?? ''}>Con excepción</span></>}</>}</td>
                    <td data-label="Desde">{x.vigente === null ? '—' : fechaHora(x.vigente.desde)}</td>
                    {gestiona && <td className="celda-accion">{x.disponible ? <button onClick={() => setDominio(x.dominio)}>Cambiar maestro</button> : <span className="suave pequena">No disponible</span>}</td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
      {dominio !== null && (
        <DialogoCambioMaestro dominio={dominio} actual={estado.datos?.dominios.find((x) => x.dominio === dominio)?.vigente?.sistema.id ?? null}
          sistemas={sistemas.filter((s) => s.activo)} cerrar={() => setDominio(null)} alCambiar={() => { setDominio(null); alCambiar(); }} />
      )}
    </div>
  );
}

const plural = (n: number, uno: string, varios: string): string => `${String(n)} ${n === 1 ? uno : varios}`;

/** Preview obligatorio: primero se calcula la cobertura; sólo se aplica ESE preview (si los datos cambian, se recalcula). */
export function DialogoCambioMaestro({ dominio, actual, sistemas, cerrar, alCambiar }: {
  dominio: DominioMaestro; actual: string | null; sistemas: readonly SistemaExterno[]; cerrar: () => void; alCambiar: () => void;
}): ReactNode {
  const { usuario } = useSesion();
  const esAdmin = usuario?.roles.some((r) => r.rol === 'ADMIN') === true;
  const [idSistema, setIdSistema] = useState('');
  const [preview, setPreview] = useState<PreviewMaestro | null>(null);
  const [motivo, setMotivo] = useState('');
  const [excepcion, setExcepcion] = useState(false);
  const [motivoExcepcion, setMotivoExcepcion] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [ocupado, setOcupado] = useState(false);
  async function calcular(id: string): Promise<void> {
    setError(null); setPreview(null); setOcupado(true);
    try { setPreview(await api.post<PreviewMaestro>(`/api/inventario/maestros-dominio/${dominio}/preview`, { idSistema: id })); } catch (e) { setError(e); } finally { setOcupado(false); }
  }
  async function aplicar(ev: FormEvent): Promise<void> {
    ev.preventDefault();
    if (preview === null) return;
    setError(null); setOcupado(true);
    try {
      await api.post(`/api/inventario/maestros-dominio/${dominio}/cambiar`, {
        idSistema, motivo, huella: preview.huella, ...(preview.requiereExcepcion ? { excepcion, motivoExcepcion } : {}),
      });
      alCambiar();
    } catch (e) {
      setError(e);
      if (e instanceof FalloApi && e.codigo === 'PREVIEW_CADUCADA') void calcular(idSistema);
    } finally {
      setOcupado(false);
    }
  }
  const p = preview;
  const bloqueado = p === null || p.bloqueos.length > 0;
  const puedeAplicar = !bloqueado && motivo.trim().length >= 3 && (!p.requiereExcepcion || (excepcion && motivoExcepcion.trim().length >= 10));
  const muestra = (t: string, filas: MuestraPreviewMaestro[] | undefined) => (filas ?? []).length === 0 ? null : (
    <details><summary>{t} ({(filas ?? []).length}{(filas ?? []).length === 50 ? '+' : ''})</summary>
      <ul className="pequena">{(filas ?? []).map((m) => <li key={m.id}><span className="mono">{m.codigoNegocio ?? m.codigo}</span>{m.codigoNuevo !== undefined && <> → <span className="mono">{m.codigoNuevo}</span></>} · {m.descripcion}</li>)}</ul>
    </details>
  );
  const que = { ARTICULOS: 'artículos activos', ALMACENES: 'almacenes activos', FAMILIAS: 'familias activas', UNIDADES: 'unidades activas' }[dominio];
  return (
    <Modal titulo={`Cambiar el maestro de ${ETIQUETA_DOMINIO[dominio].toLowerCase()}`} cerrar={cerrar}>
      <form onSubmit={(ev) => void aplicar(ev)} aria-label="Cambiar sistema maestro">
        <div className="campo"><label htmlFor="cm-sistema">Nuevo sistema maestro</label>
          <select id="cm-sistema" value={idSistema} onChange={(ev) => { setIdSistema(ev.target.value); if (ev.target.value !== '') void calcular(ev.target.value); else setPreview(null); }}>
            <option value="">Elige el sistema…</option>
            {sistemas.filter((s) => s.id !== actual).map((s) => <option key={s.id} value={s.id}>{s.nombre} ({s.codigo})</option>)}
          </select></div>
        {ocupado && p === null && <p className="suave" role="status">Calculando la cobertura…</p>}
        {p !== null && (
          <section aria-label="Preview del cambio" className="panel-cuerpo">
            {(dominio === 'ARTICULOS' || dominio === 'ALMACENES') && p.total !== undefined && (
              <p><strong>Cobertura: {p.conCodigo ?? 0}/{p.total}</strong> {que} tienen código en {p.sistemaNuevo.codigo}
                {(p.sinCodigo ?? 0) > 0 && <> · {p.sinCodigo} sin código</>}{(p.colisiones ?? 0) > 0 && <> · {plural(p.colisiones ?? 0, 'colisión', 'colisiones')}</>}
                {(p.referenciasInactivas ?? 0) > 0 && <> · {p.referenciasInactivas} con referencia inactiva</>}
                {(p.reutilizaciones ?? 0) > 0 && <> · {plural(p.reutilizaciones ?? 0, 'reutilización', 'reutilizaciones')}</>}
                {' '}· cambiará el código de {p.cambios}.</p>
            )}
            {(dominio === 'FAMILIAS' || dominio === 'UNIDADES') && (
              <p>Las {que} con referencia en {p.sistemaNuevo.codigo} pasan a llamarse como en él ({p.cambios} cambian de código, auditado); el resto conserva su código.
                {(p.colisiones ?? 0) > 0 && <> {plural(p.colisiones ?? 0, 'colisión', 'colisiones')}.</>}</p>
            )}
            {p.bloqueos.map((b) => <p key={b} className="nota error" role="alert">{TEXTO_BLOQUEO[b] ?? b}</p>)}
            {p.motivosExcepcion.map((m) => <p key={m} className="nota aviso">{TEXTO_EXCEPCION[m] ?? m}</p>)}
            {muestra('Sin código', p.muestraSinCodigo)}
            {muestra('Colisiones', p.muestraColisiones)}
            {muestra('Reutilizaciones', p.muestraReutilizaciones)}
            {muestra('Cambios de código', p.muestraCambios)}
          </section>
        )}
        {p !== null && !bloqueado && (
          <>
            <div className="campo"><label htmlFor="cm-motivo">Motivo del cambio</label>
              <input id="cm-motivo" required minLength={3} maxLength={500} value={motivo} onChange={(ev) => setMotivo(ev.target.value)} /></div>
            {p.requiereExcepcion && (esAdmin ? (
              <>
                <label className="fila"><input type="checkbox" checked={excepcion} onChange={(ev) => setExcepcion(ev.target.checked)} /> Seguir con excepción (queda registrado)</label>
                {excepcion && <div className="campo"><label htmlFor="cm-exc">Motivo de la excepción</label>
                  <input id="cm-exc" minLength={10} maxLength={500} value={motivoExcepcion} onChange={(ev) => setMotivoExcepcion(ev.target.value)} /></div>}
              </>
            ) : <p className="nota aviso">Con cobertura incompleta, sólo un administrador puede seguir.</p>)}
          </>
        )}
        {error !== null && <ErrorCaja error={error} />}
        <div className="fila"><button type="submit" className="principal" disabled={!puedeAplicar || ocupado}>Cambiar maestro</button><button type="button" onClick={cerrar}>Cancelar</button></div>
      </form>
    </Modal>
  );
}

// ================================================================ referencias de unidades y familias

function SeccionReferencias({ sistemas }: { sistemas: readonly SistemaExterno[] }): ReactNode {
  const { puede } = useSesion();
  const [elegido, setIdSistema] = useState('');
  const idSistema = elegido !== '' ? elegido : sistemas[0]?.id ?? '';
  const opciones = useDatos(() => api.get<OpcionesInventario>('/api/inventario/opciones'), []);
  if (sistemas.length === 0) return <div className="panel"><Vacio titulo="No hay sistemas de origen" /></div>;
  return (
    <>
      <div className="panel">
        <div className="panel-cuerpo">
          <p className="suave pequena">El código de una unidad o familia en cada sistema (SAP KG, SAGE KIL → KG de NeatOS). Una por registro y sistema; cambiarla deja la anterior en el
            histórico. También se crean al importar. Las de artículos y almacenes están en su ficha y en sus importaciones.</p>
          <label className="campo"><span>Sistema</span>
            <select value={idSistema} onChange={(ev) => setIdSistema(ev.target.value)}>
              {sistemas.map((s) => <option key={s.id} value={s.id}>{s.nombre} ({s.codigo})</option>)}
            </select>
          </label>
        </div>
      </div>
      {idSistema !== '' && opciones.datos !== null && (
        <>
          <ListaReferencias idSistema={idSistema} clase="unidades" titulo="Unidades" edita={puede('articulos:referencias')}
            destinos={opciones.datos.unidades.map((u) => ({ id: u.id, texto: `${u.codigo} — ${u.descripcion}` }))} />
          <ListaReferencias idSistema={idSistema} clase="familias" titulo="Familias" edita={puede('articulos:referencias')}
            destinos={opciones.datos.familias.map((f: FamiliaArticulo) => ({ id: f.id, texto: `${f.codigo} — ${f.descripcion} (${f.tipo.descripcion})` }))} />
        </>
      )}
    </>
  );
}

function ListaReferencias({ idSistema, clase, titulo, edita, destinos }: {
  idSistema: string; clase: 'unidades' | 'familias'; titulo: string; edita: boolean; destinos: readonly { id: string; texto: string }[];
}): ReactNode {
  const ruta = `/api/inventario/sistemas-externos/${idSistema}/equivalencias/${clase}`;
  const lista = useDatos(() => api.get<{ filas: AliasExterno[] }>(ruta), [ruta]);
  const [nuevo, setNuevo] = useState({ codigoExterno: '', idDestino: '' });
  const [error, setError] = useState<unknown>(null);
  async function guardar(ev: FormEvent): Promise<void> {
    ev.preventDefault();
    setError(null);
    try {
      await api.put(ruta, nuevo);
      setNuevo({ codigoExterno: '', idDestino: '' });
      lista.recargar();
    } catch (e) {
      setError(e);
    }
  }
  return (
    <div className="panel">
      <div className="panel-cabecera"><h2>{titulo}</h2></div>
      {error !== null && <ErrorCaja error={error} />}
      <Panel estado={lista}>
        {(d) => (d.filas.length === 0 ? <Vacio titulo="Sin referencias: se usa el mismo código que en NeatOS" /> : (
          <div className="tabla-desliza">
            <table className="tabla-tarjetas" aria-label={`Referencias de ${titulo.toLowerCase()}`}>
              <thead><tr><th>Código en el sistema</th><th>En NeatOS</th></tr></thead>
              <tbody>{d.filas.map((a) => <tr key={a.id}><td className="mono celda-titulo">{a.codigoExterno}</td><td data-label="En NeatOS">{a.destino.codigo} — {a.destino.descripcion}{a.destino.tipo !== undefined ? ` (${a.destino.tipo})` : ''}</td></tr>)}</tbody>
            </table>
          </div>
        ))}
      </Panel>
      {edita && (
        <form className="panel-cuerpo fila" style={{ flexWrap: 'wrap', alignItems: 'flex-end' }} onSubmit={(ev) => void guardar(ev)} aria-label={`Nueva referencia de ${titulo.toLowerCase()}`}>
          <label className="campo"><span>Código en el sistema</span><input value={nuevo.codigoExterno} required onChange={(ev) => setNuevo({ ...nuevo, codigoExterno: ev.target.value })} /></label>
          <label className="campo"><span>En NeatOS</span>
            <select value={nuevo.idDestino} required onChange={(ev) => setNuevo({ ...nuevo, idDestino: ev.target.value })}>
              <option value="">Elige…</option>
              {destinos.map((x) => <option key={x.id} value={x.id}>{x.texto}</option>)}
            </select>
          </label>
          <button type="submit">Guardar</button>
        </form>
      )}
    </div>
  );
}
